import { K } from "./kernels.js";

/* ===========================================================================
   Polygon geometry.
   =========================================================================== */

/** Shoelace area of a flat [x0,y0,x1,y1,...] ring. */
export function contourArea(pts) {
  let area = 0;
  for (let i = 0, n = pts.length; i < n; i += 2) {
    const j = (i + 2) % n;
    area += pts[i] * pts[j + 1] - pts[j] * pts[i + 1];
  }
  return Math.abs(area) / 2;
}

export function arcLength(pts) {
  let total = 0;
  for (let i = 0, n = pts.length; i < n; i += 2) {
    const j = (i + 2) % n;
    total += Math.hypot(pts[j] - pts[i], pts[j + 1] - pts[i + 1]);
  }
  return total;
}

/**
 * Douglas-Peucker over an open chain of indices into `pts`.
 *
 * Iterative rather than recursive: a traced contour can run to tens of thousands of
 * points, and the split is data-dependent, so a recursive version is one noisy edge map
 * away from overflowing the stack. Pushing the right segment before the left reproduces
 * the recursion's output order exactly.
 */
export function douglasPeucker(pts, from, to, epsilon, out) {
  const stack = [[from, to]];
  while (stack.length) {
    const [a, b] = stack.pop();
    const ax = pts[a * 2],
      ay = pts[a * 2 + 1];
    const bx = pts[b * 2],
      by = pts[b * 2 + 1];
    const dx = bx - ax,
      dy = by - ay;
    const norm = Math.hypot(dx, dy);
    let worst = -1,
      worstIndex = -1;
    for (let i = a + 1; i < b; i++) {
      const px = pts[i * 2],
        py = pts[i * 2 + 1];
      const d =
        norm < 1e-9
          ? Math.hypot(px - ax, py - ay)
          : Math.abs(dy * (px - ax) - dx * (py - ay)) / norm;
      if (d > worst) {
        worst = d;
        worstIndex = i;
      }
    }
    if (worst > epsilon && worstIndex > a) {
      stack.push([worstIndex, b]);
      stack.push([a, worstIndex]);
    } else {
      out.push(a);
    }
  }
}

/**
 * cv2.approxPolyDP for a closed contour: split at the two mutually farthest vertices so
 * the simplification is independent of where the trace happened to start, then run
 * Douglas-Peucker over each arc.
 */
export function approxPolyClosed(pts, epsilon) {
  const n = pts.length / 2;
  if (n < 4) return null;
  const far = (from) => {
    let best = -1,
      index = from;
    for (let i = 0; i < n; i++) {
      const d = Math.hypot(pts[i * 2] - pts[from * 2], pts[i * 2 + 1] - pts[from * 2 + 1]);
      if (d > best) {
        best = d;
        index = i;
      }
    }
    return index;
  };
  const a = far(0),
    b = far(a);
  const lo = Math.min(a, b),
    hi = Math.max(a, b);

  const first = [];
  douglasPeucker(pts, lo, hi, epsilon, first);
  first.push(hi);

  // The wrapping arc, re-indexed onto a temporary linear buffer.
  const wrapped = [];
  for (let i = hi; i <= lo + n; i++) {
    const k = i % n;
    wrapped.push(pts[k * 2], pts[k * 2 + 1]);
  }
  const second = [];
  douglasPeucker(wrapped, 0, wrapped.length / 2 - 1, epsilon, second);

  const result = [];
  for (const i of first) result.push(pts[i * 2], pts[i * 2 + 1]);
  for (let k = 1; k < second.length; k++) {
    const i = second[k];
    result.push(wrapped[i * 2], wrapped[i * 2 + 1]);
  }
  return result;
}

export function isConvexRing(pts) {
  const n = pts.length / 2;
  if (n < 3) return false;
  let positive = false,
    negative = false;
  for (let i = 0; i < n; i++) {
    const ax = pts[((i + 1) % n) * 2] - pts[i * 2];
    const ay = pts[((i + 1) % n) * 2 + 1] - pts[i * 2 + 1];
    const bx = pts[((i + 2) % n) * 2] - pts[((i + 1) % n) * 2];
    const by = pts[((i + 2) % n) * 2 + 1] - pts[((i + 1) % n) * 2 + 1];
    const cross = ax * by - ay * bx;
    if (cross > 0) positive = true;
    else if (cross < 0) negative = true;
    if (positive && negative) return false;
  }
  return true;
}

export const APPROX_FACTORS = [1.0, 1.5, 2.0, 2.75];

/**
 * Reduce a contour to four convex corners, loosening the tolerance if needed —
 * CardDetector._approximate, including its epsilon ladder. `factors` narrows the
 * ladder for callers that want only the trustworthy tight reading.
 */
export function approximateQuad(pts, epsilon, factors = APPROX_FACTORS) {
  const perimeter = K.arcLength(pts);
  if (perimeter <= 0) return null;
  for (const factor of factors) {
    const approx = K.approxPolyClosed(pts, epsilon * factor * perimeter);
    if (approx && approx.length === 8 && isConvexRing(approx)) return Float32Array.from(approx);
  }
  return null;
}

/**
 * A quad rebuilt from the four longest sides of a broken outline —
 * CardDetector._sides_quad.
 *
 * The occlusion case direct approximation cannot answer: fingers over the border, a
 * neighbouring object touching the edge. The polygon then has five to ten vertices —
 * four card corners plus the excursion — but the card's own sides are still its
 * longest edges, and a thumb splitting one side leaves its longer fragment collinear
 * with the true edge, so intersecting the four dominant lines recovers the corner the
 * polygon never had. Kept in the polygon's cyclic order so consecutive lines really
 * are adjacent sides; two fragments of one interrupted side come out near-parallel
 * and decline at the intersection.
 */
export function sidesQuad(pts, epsilon) {
  const perimeter = K.arcLength(pts);
  if (perimeter <= 0) return null;
  let polygon = null;
  for (const factor of [1.0, 1.5, 2.0]) {
    const approx = K.approxPolyClosed(pts, epsilon * factor * perimeter);
    if (approx && approx.length >= 10 && approx.length <= 20) {
      polygon = approx;
      break;
    }
  }
  if (!polygon) return null;

  const n = polygon.length / 2;
  const vx = new Float32Array(n),
    vy = new Float32Array(n),
    lengths = new Float32Array(n);
  let total = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    vx[i] = polygon[j * 2] - polygon[i * 2];
    vy[i] = polygon[j * 2 + 1] - polygon[i * 2 + 1];
    lengths[i] = Math.hypot(vx[i], vy[i]);
    total += lengths[i];
  }
  // Four longest sides, back in cyclic order.
  const dominant = Array.from({ length: n }, (_, i) => i)
    .sort((a, b) => lengths[b] - lengths[a])
    .slice(0, 4)
    .sort((a, b) => a - b);
  // A side too short to anchor a line fit is an excursion edge, not a card side.
  let dominantLength = 0,
    shortest = Infinity;
  for (const i of dominant) {
    dominantLength += lengths[i];
    if (lengths[i] < shortest) shortest = lengths[i];
  }
  if (shortest < 0.05 * perimeter) return null;
  // The four dominant sides must *be* the outline, near enough: when the skipped
  // edges are a large share of it, part of the card's own boundary is being discarded
  // and a fitted side would run along the occluder instead — decline.
  if (total - dominantLength > 0.1 * total) return null;

  const lines = dominant.map((i) => [
    vx[i] / lengths[i],
    vy[i] / lengths[i],
    polygon[i * 2],
    polygon[i * 2 + 1],
  ]);
  const quad = new Float32Array(8);
  for (let j = 0; j < 4; j++) {
    const corner = intersectLines(lines[(j + 3) % 4], lines[j]);
    if (!corner) return null;
    quad[j * 2] = corner[0];
    quad[j * 2 + 1] = corner[1];
  }
  return isStrictlyConvexQuad(quad) ? quad : null;
}

/** Andrew's monotone chain. */
export function convexHull(pts) {
  const n = pts.length / 2;
  const order = Array.from({ length: n }, (_, i) => i).sort(
    (a, b) => pts[a * 2] - pts[b * 2] || pts[a * 2 + 1] - pts[b * 2 + 1],
  );
  const cross = (o, a, b) =>
    (pts[a * 2] - pts[o * 2]) * (pts[b * 2 + 1] - pts[o * 2 + 1]) -
    (pts[a * 2 + 1] - pts[o * 2 + 1]) * (pts[b * 2] - pts[o * 2]);
  const build = (source) => {
    const chain = [];
    for (const i of source) {
      while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], i) <= 0)
        chain.pop();
      chain.push(i);
    }
    chain.pop();
    return chain;
  };
  const hull = build(order).concat(build(order.slice().reverse()));
  const out = [];
  for (const i of hull) out.push(pts[i * 2], pts[i * 2 + 1]);
  return out;
}

/** cv2.minAreaRect via rotating calipers over the hull, returned as four corners. */
export function minAreaRect(pts) {
  const hull = K.convexHull(pts);
  const n = hull.length / 2;
  if (n < 3) return null;
  let best = null;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ex = hull[j * 2] - hull[i * 2],
      ey = hull[j * 2 + 1] - hull[i * 2 + 1];
    const len = Math.hypot(ex, ey);
    if (len < 1e-9) continue;
    const ux = ex / len,
      uy = ey / len;
    let minU = Infinity,
      maxU = -Infinity,
      minV = Infinity,
      maxV = -Infinity;
    for (let k = 0; k < n; k++) {
      const u = hull[k * 2] * ux + hull[k * 2 + 1] * uy;
      const v = -hull[k * 2] * uy + hull[k * 2 + 1] * ux;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const area = (maxU - minU) * (maxV - minV);
    if (!best || area < best.area) best = { area, ux, uy, minU, maxU, minV, maxV };
  }
  if (!best) return null;
  const { ux, uy, minU, maxU, minV, maxV } = best;
  const point = (u, v) => [u * ux - v * uy, u * uy + v * ux];
  return Float32Array.from([
    ...point(minU, minV),
    ...point(maxU, minV),
    ...point(maxU, maxV),
    ...point(minU, maxV),
  ]);
}

/** Sutherland-Hodgman clip of one convex quad by another, then shoelace. */
export function convexIntersectionArea(a, b) {
  let poly = [];
  for (let i = 0; i < 8; i += 2) poly.push([a[i], a[i + 1]]);
  // Orient the clipper counter-clockwise so "inside" is a consistent sign.
  const signedArea = (ring) => {
    let s = 0;
    for (let i = 0; i < ring.length; i++) {
      const j = (i + 1) % ring.length;
      s += ring[i][0] * ring[j][1] - ring[j][0] * ring[i][1];
    }
    return s / 2;
  };
  let clip = [];
  for (let i = 0; i < 8; i += 2) clip.push([b[i], b[i + 1]]);
  if (signedArea(clip) < 0) clip.reverse();
  if (signedArea(poly) < 0) poly.reverse();

  for (let c = 0; c < clip.length && poly.length; c++) {
    const p1 = clip[c],
      p2 = clip[(c + 1) % clip.length];
    const side = (p) => (p2[0] - p1[0]) * (p[1] - p1[1]) - (p2[1] - p1[1]) * (p[0] - p1[0]);
    const next = [];
    for (let i = 0; i < poly.length; i++) {
      const cur = poly[i],
        prev = poly[(i + poly.length - 1) % poly.length];
      const sc = side(cur),
        sp = side(prev);
      if (sc >= 0) {
        if (sp < 0) {
          const t = sp / (sp - sc);
          next.push([prev[0] + t * (cur[0] - prev[0]), prev[1] + t * (cur[1] - prev[1])]);
        }
        next.push(cur);
      } else if (sp >= 0) {
        const t = sp / (sp - sc);
        next.push([prev[0] + t * (cur[0] - prev[0]), prev[1] + t * (cur[1] - prev[1])]);
      }
    }
    poly = next;
  }
  return poly.length < 3 ? 0 : Math.abs(signedArea(poly));
}

export const quadArea = (q) => K.contourArea(q);

export function quadOverlap(a, b) {
  const smaller = Math.min(quadArea(a), quadArea(b));
  return smaller > 1e-6 ? convexIntersectionArea(a, b) / smaller : 0;
}

/**
 * normalize.order_corners — geometric, not positional, so it is valid at any in-plane
 * rotation. Sort clockwise about the centroid, roll so the long edges become the left
 * and right sides, then break the remaining 180-degree tie toward top-left.
 */
export function orderCorners(points) {
  const pts = [];
  for (let i = 0; i < 8; i += 2) pts.push([points[i], points[i + 1]]);
  const cx = (pts[0][0] + pts[1][0] + pts[2][0] + pts[3][0]) / 4;
  const cy = (pts[0][1] + pts[1][1] + pts[2][1] + pts[3][1]) / 4;
  const cycle = pts
    .slice()
    .sort((p, q) => Math.atan2(p[1] - cy, p[0] - cx) - Math.atan2(q[1] - cy, q[0] - cx));

  const verticalness = (quad) => {
    const [tl, tr, br, bl] = quad;
    const vertical =
      Math.hypot(bl[0] - tl[0], bl[1] - tl[1]) + Math.hypot(br[0] - tr[0], br[1] - tr[1]);
    const horizontal =
      Math.hypot(tr[0] - tl[0], tr[1] - tl[1]) + Math.hypot(br[0] - bl[0], br[1] - bl[1]);
    return vertical - horizontal;
  };
  const rolls = [0, 1, 2, 3].map((s) => cycle.slice(s).concat(cycle.slice(0, s)));
  rolls.sort((a, b) => verticalness(b) - verticalness(a));
  const [upright, flipped] = rolls;
  const chosen = upright[0][0] + upright[0][1] <= flipped[0][0] + flipped[0][1] ? upright : flipped;
  return Float32Array.from(chosen.flat());
}

export function edgeLengths(q) {
  const [tlx, tly, trx, try_, brx, bry, blx, bly] = q;
  const width = (Math.hypot(trx - tlx, try_ - tly) + Math.hypot(brx - blx, bry - bly)) / 2;
  const height = (Math.hypot(blx - tlx, bly - tly) + Math.hypot(brx - trx, bry - try_)) / 2;
  return [width, height];
}

/** Intersection of two lines in (vx, vy, x0, y0) form; null when near-parallel. */
export function intersectLines(a, b) {
  const cross = a[0] * b[1] - a[1] * b[0];
  if (Math.abs(cross) < 1e-9) return null;
  const t = ((b[2] - a[2]) * b[1] - (b[3] - a[3]) * b[0]) / cross;
  return [a[2] + t * a[0], a[3] + t * a[1]];
}

/**
 * Strict convexity for a quad — `detect._quad_is_convex`.
 *
 * Distinct from `isConvexRing`, which mirrors `cv2.isContourConvex` and tolerates a zero
 * cross product. Python applies the strict form to the two places that *construct* a
 * quad by fitting — corner refinement and the sides fit — because there a zero cross
 * means three collinear corners, which is a degenerate reading rather than a convex one.
 * Accepting it is how a collapsed quad reached the warp in the browser and nowhere else.
 */
export function isStrictlyConvexQuad(quad) {
  let positive = 0,
    negative = 0;
  for (let i = 0; i < 4; i++) {
    const ax = quad[((i + 1) % 4) * 2] - quad[i * 2];
    const ay = quad[((i + 1) % 4) * 2 + 1] - quad[i * 2 + 1];
    const bx = quad[((i + 2) % 4) * 2] - quad[((i + 1) % 4) * 2];
    const by = quad[((i + 2) % 4) * 2 + 1] - quad[((i + 1) % 4) * 2 + 1];
    const cross = ax * by - ay * bx;
    if (cross > 0) positive++;
    else if (cross < 0) negative++;
  }
  return positive === 4 || negative === 4;
}
